import { message } from '@repo/ui';
import { nodesSelector } from 'automations/selectors/reactFlow';
import { didNodeSyncToServer } from 'automations/utils/node';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useBlocker } from 'react-router-dom';

/**
 * Hook to report unsaved changes to the user before leaving the page
 * Tracks all the nodes and checks if any node is not synced to BE
 */
export default function useReportUnsavedChanges() {
  const nodes = useSelector(nodesSelector);

  const haveUnsavedChanges = useMemo(() => nodes.some((node) => !didNodeSyncToServer(node)), [nodes]);
  const shouldBlock = useMemo(() => haveUnsavedChanges && nodes.length > 1, [haveUnsavedChanges, nodes.length]);

  // Handle React Router navigation
  useBlocker(({ currentLocation, nextLocation }) => {
    if (shouldBlock && currentLocation.pathname !== nextLocation.pathname) {
      message.warning('You have some unsaved changes. Please save the nodes before leaving the page.');
      return true;
    }
    return false;
  });

  // Handle browser navigation/refresh
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldBlock) {
        event.preventDefault();
        message.warning('You have some unsaved changes. Please save the nodes before leaving the page.');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldBlock]);

  return null;
}
